import { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Phone, Calendar, UserX, UserCheck, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembersPage = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dob: '',
    address: '',
    email: '',
    membership_id: '1m',
    join_date: new Date().toISOString().split('T')[0],
    discount: 0,
    notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash'
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMembers();
    fetchMemberships();
  }, [token, showActiveOnly]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members?active_only=${showActiveOnly}`, { headers });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      const response = await axios.get(`${API}/memberships`, { headers });
      setMemberships(response.data);
    } catch (error) {
      console.error('Error fetching memberships:', error);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members`, formData, { headers });
      toast.success('Member added successfully');
      setShowAddModal(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/members/${selectedMember.id}`, formData, { headers });
      toast.success('Member updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      await axios.delete(`${API}/members/${memberId}`, { headers });
      toast.success('Member deleted successfully');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const handleToggleActive = async (member) => {
    try {
      await axios.put(`${API}/members/${member.id}`, { is_active: !member.is_active }, { headers });
      toast.success(`Member ${member.is_active ? 'marked inactive' : 'marked active'}`);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to update member status');
    }
  };

  const handleMarkAsPaid = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        member_id: selectedMember.id,
        amount: paymentData.amount,
        payment_date: paymentData.payment_date,
        payment_mode: paymentData.payment_mode
      }, { headers });
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentModal = (member) => {
    setSelectedMember(member);
    setPaymentData({
      amount: member.final_amount,
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash'
    });
    setShowPaymentModal(true);
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      phone: member.phone,
      dob: member.dob || '',
      address: member.address || '',
      email: member.email || '',
      membership_id: member.membership_id,
      join_date: member.join_date,
      discount: member.discount,
      notes: member.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      dob: '',
      address: '',
      email: '',
      membership_id: '1m',
      join_date: new Date().toISOString().split('T')[0],
      discount: 0,
      notes: ''
    });
    setSelectedMember(null);
  };

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.phone.includes(searchQuery)
  );

  const getStatusBadge = (isActive) => {
    if (!isActive) return <span className="badge-expired px-2 py-1 text-xs">Inactive</span>;
    return <span className="badge-active px-2 py-1 text-xs">Active</span>;
  };

  const getMembershipPrice = (membershipId) => {
    const membership = memberships.find(m => m.id === membershipId);
    return membership?.price || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading members...</div>
      </div>
    );
  }

  return (
    <div data-testid="members-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-extrabold text-3xl lg:text-4xl uppercase tracking-tight text-white">
            Members
          </h1>
          <p className="text-zinc-400 mt-1">{members.length} {showActiveOnly ? 'active' : 'total'} members</p>
        </div>
        <Button
          data-testid="add-member-modal-btn"
          onClick={() => setShowAddModal(true)}
          className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            data-testid="search-members-input"
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-dark pl-10"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-zinc-400 text-sm">Show Active Only</span>
          <Switch 
            checked={showActiveOnly}
            onCheckedChange={setShowActiveOnly}
            data-testid="active-filter-switch"
          />
        </div>
      </div>

      {/* Members Table */}
      <div className="card-glass overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Member</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Phone</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold hidden md:table-cell">Membership</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold hidden lg:table-cell">Amount</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Status</th>
                <th className="text-right p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => (
                <tr
                  key={member.id}
                  data-testid={`member-row-${member.id}`}
                  className={`border-b border-zinc-800/50 table-row-hover ${!member.is_active ? 'opacity-60' : ''}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${member.is_active ? 'bg-[#FF3B30]/20' : 'bg-zinc-700/20'} flex items-center justify-center`}>
                        <span className={`font-['Barlow_Condensed'] font-bold ${member.is_active ? 'text-[#FF3B30]' : 'text-zinc-500'}`}>
                          {member.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{member.name}</p>
                        {member.email && <p className="text-xs text-zinc-500">{member.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <a href={`tel:${member.phone}`} className="text-zinc-300 hover:text-[#FF3B30] flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {member.phone}
                    </a>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="text-zinc-300">{member.membership_name}</span>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="font-['Barlow_Condensed'] font-bold text-white">₹{member.final_amount.toLocaleString()}</span>
                  </td>
                  <td className="p-4">
                    {getStatusBadge(member.is_active)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        data-testid={`mark-paid-${member.id}`}
                        onClick={() => openPaymentModal(member)}
                        variant="ghost"
                        size="sm"
                        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 p-2"
                        title="Mark as Paid"
                      >
                        <IndianRupee className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`toggle-active-${member.id}`}
                        onClick={() => handleToggleActive(member)}
                        variant="ghost"
                        size="sm"
                        className={`p-2 ${member.is_active ? 'text-zinc-400 hover:text-amber-400' : 'text-zinc-400 hover:text-emerald-400'}`}
                        title={member.is_active ? 'Mark Inactive' : 'Mark Active'}
                      >
                        {member.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </Button>
                      <Button
                        data-testid={`edit-member-${member.id}`}
                        onClick={() => openEditModal(member)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white p-2"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-member-${member.id}`}
                        onClick={() => handleDeleteMember(member.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-400 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">No members found</p>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] font-bold text-2xl uppercase text-white">
              Add New Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group col-span-2">
                <Label className="form-label">Full Name *</Label>
                <Input
                  data-testid="member-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Phone Number *</Label>
                <Input
                  data-testid="member-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark"
                  placeholder="9876543210"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Date of Birth</Label>
                <Input
                  data-testid="member-dob-input"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group col-span-2">
                <Label className="form-label">Email</Label>
                <Input
                  data-testid="member-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group col-span-2">
                <Label className="form-label">Address</Label>
                <Input
                  data-testid="member-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Membership Plan *</Label>
                <Select
                  value={formData.membership_id}
                  onValueChange={(value) => setFormData({ ...formData, membership_id: value })}
                >
                  <SelectTrigger data-testid="member-membership-select" className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {memberships.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-white hover:bg-zinc-800">
                        {m.name} - ₹{m.price.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label className="form-label">Join Date *</Label>
                <Input
                  data-testid="member-join-date-input"
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                  className="input-dark"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Discount (₹)</Label>
                <Input
                  data-testid="member-discount-input"
                  type="number"
                  min="0"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                  className="input-dark"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Final Amount</Label>
                <div className="h-12 flex items-center px-3 bg-zinc-950 border border-zinc-800 text-white font-['Barlow_Condensed'] font-bold text-xl">
                  ₹{Math.max(0, getMembershipPrice(formData.membership_id) - formData.discount).toLocaleString()}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-add-member-btn"
                type="submit"
                className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
              >
                Add Member
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] font-bold text-2xl uppercase text-white">
              Edit Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditMember} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group col-span-2">
                <Label className="form-label">Full Name *</Label>
                <Input
                  data-testid="edit-member-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Phone Number *</Label>
                <Input
                  data-testid="edit-member-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark"
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Date of Birth</Label>
                <Input
                  data-testid="edit-member-dob-input"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group col-span-2">
                <Label className="form-label">Email</Label>
                <Input
                  data-testid="edit-member-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group col-span-2">
                <Label className="form-label">Address</Label>
                <Input
                  data-testid="edit-member-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Membership Plan</Label>
                <Select
                  value={formData.membership_id}
                  onValueChange={(value) => setFormData({ ...formData, membership_id: value })}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {memberships.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-white hover:bg-zinc-800">
                        {m.name} - ₹{m.price.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label className="form-label">Discount (₹)</Label>
                <Input
                  data-testid="edit-member-discount-input"
                  type="number"
                  min="0"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                  className="input-dark"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setShowEditModal(false); resetForm(); }}
                className="text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-edit-member-btn"
                type="submit"
                className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] font-bold text-2xl uppercase text-white">
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMarkAsPaid} className="space-y-4">
            {selectedMember && (
              <div className="p-4 bg-zinc-900/50 border border-zinc-800">
                <p className="text-white font-medium">{selectedMember.name}</p>
                <p className="text-zinc-500 text-sm">{selectedMember.membership_name} - ₹{selectedMember.final_amount.toLocaleString()}</p>
              </div>
            )}
            <div className="form-group">
              <Label className="form-label">Amount (₹) *</Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                min="0"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                className="input-dark"
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Payment Date *</Label>
              <Input
                data-testid="payment-date-input"
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                className="input-dark"
                required
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Payment Mode *</Label>
              <Select
                value={paymentData.payment_mode}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_mode: value })}
              >
                <SelectTrigger data-testid="payment-mode-select" className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="cash" className="text-white hover:bg-zinc-800">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-zinc-800">UPI</SelectItem>
                  <SelectItem value="card" className="text-white hover:bg-zinc-800">Card</SelectItem>
                  <SelectItem value="bank_transfer" className="text-white hover:bg-zinc-800">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowPaymentModal(false)}
                className="text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-payment-btn"
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-wider rounded-none"
              >
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersPage;
